import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, TextField, Table, TableHead, TableBody, TableRow, TableCell, Checkbox } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';
import axiosInstance from '../axiosConfig';
import API_URL from '../config';
import './Checkout.css';

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { bookId, pagesCount, emptyPagesToAdd } = location.state || {};
  const [numCopies, setNumCopies] = useState(1);
  const [numCopiesError, setNumCopiesError] = useState(false);
  const [pricePerCopy, setPricePerCopy] = useState(null);
  const [totalPrice, setTotalPrice] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [finalPrice, setFinalPrice] = useState(null);
  const [taxAmount, setTaxAmount] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(null); // null, 'loading', 'success', 'error'
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [reviewConfirmed, setReviewConfirmed] = useState(false);

  useEffect(() => {
    if (!numCopies || !pagesCount) return;
  
    const fetchPriceFromBackend = async () => {
      const token = localStorage.getItem('authToken');
      try {
        const response = await axiosInstance.post('/prices/calculate', {
          copies: numCopies,
          pagesCount: pagesCount,
        }, {
          headers: { Authorization: `${token}` }
        });
  
        // Preiswerte aus dem Backend setzen
        const { pricePerCopy, totalPrice, discount, finalPrice, taxAmount } = response.data;
        setPricePerCopy(pricePerCopy);
        setTotalPrice(totalPrice);
        setDiscount(discount);
        setFinalPrice(finalPrice);
        setTaxAmount(taxAmount);
  
      } catch (error) {
        console.error("Fehler beim Abrufen des Preises vom Backend:", error);
        alert("Fehler beim Abrufen der Preisinformationen.");
        navigate('/pages/configurator');
      }
    };
  
    fetchPriceFromBackend();
  }, [numCopies, pagesCount]);  

  const handleConfirmOrder = async () => {
    if (!numCopies || isNaN(numCopies) || parseInt(numCopies) < 1) {
      setNumCopiesError(true);
      return;
    }
    setNumCopiesError(false);
    setLoadingStatus('loading');
  
    const token = localStorage.getItem('authToken');
  
    try {
      const response = await axiosInstance.post(`/doc/print/${bookId}`, {
        copies: numCopies,
        pagesCount: pagesCount,
        emptyPagesToAdd: emptyPagesToAdd
      }, {
        headers: { Authorization: `${token}` }
      });
  
      if (response.data.pdfUrl) {
        window.open(response.data.pdfUrl, '_blank'); // Öffnet die PDF-Datei in einem neuen Tab
      }
      setLoadingStatus('success'); // Erfolgreich abgeschlossen
    } catch (error) {
      console.error('Fehler beim Erstellen der PDF:', error);
      setLoadingStatus('error'); // Fehler aufgetreten
    }
  };  

  if (loadingStatus) return <LoadingScreen status={loadingStatus} />;

  return (
    <Box sx={{ maxWidth: 1000, margin: 'auto', padding: 3 }}>
      <Paper elevation={3} sx={{ padding: 3 }}>
        <Typography variant="h5" gutterBottom> Kostenübersicht </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', mt: '1rem' }}>
          <Typography variant="subtitle1"> Anzahl gewünschter Exemplare: </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <TextField
            label="Anzahl"
            type="number"
            value={numCopies}
            onChange={(e) => {
              const value = e.target.value;

              if (value === "") {
                setNumCopies("");
                setNumCopiesError(true);
                return;
              }

              const numValue = Number(value);
              if (!isNaN(numValue) && numValue > 0) {
                setNumCopies(numValue);
                setNumCopiesError(false);
              } else {
                setNumCopies(value);
                setNumCopiesError(true);
              }
            }}
            error={numCopiesError}
            inputProps={{ min: 1 }}
            variant="outlined"
            sx={{
              flex: 1,
              maxWidth: '100px',
              '& .MuiInputBase-root': {
                height: '32px', // Höhe des Eingabefelds verringert
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center', // Stellt sicher, dass der Text vertikal mittig bleibt
              },
              '& .MuiInputLabel-root': {
                fontSize: '14px',
                transform: 'translate(10px, 6px) scale(1)', // Position des Labels anpassen
              },
              '& .MuiInputLabel-shrink': {
                transform: 'translate(10px, -8px) scale(0.85)', // Verhindert Verschiebung im Fehlerfall
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: numCopiesError ? 'red' : 'inherit', // Fehlerhafte Eingabe rot markieren
                },
              },
            }}
          />
          {numCopiesError && (
            <Typography color="error" fontSize="14px">
              Bitte geben Sie eine gültige Anzahl an Exemplaren ein.
            </Typography>
          )}
        </Box>
        </Box>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell><strong>Anzahl Seiten Ihres Schullogbuchs:</strong></TableCell>
              <TableCell align="right">{pagesCount}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Stückpreis pro Exemplar:</strong></TableCell>
              <TableCell align="right">{pricePerCopy?.toFixed(2)} €</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Typography variant="body2" sx={{ color: "gray", fontStyle: "italic", fontSize: "0.85rem", mt: 0.5 }}>
          Eine aktuelle Preisübersicht für den Druck Ihrer Schullogbücher finden Sie in unserem{' '}
          <a href="/pages/info" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline" }}>
            Informationszentrum
          </a>.
        </Typography>
        <Box sx={{ marginTop: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Beschreibung</strong></TableCell>
                <TableCell align="right"><strong>Menge</strong></TableCell>
                <TableCell align="right"><strong>Stückpreis</strong></TableCell>
                <TableCell align="right"><strong>Gesamt</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Betrag:</TableCell>
                <TableCell align="right">{numCopies}</TableCell>
                <TableCell align="right">{pricePerCopy?.toFixed(2)} €</TableCell>
                <TableCell align="right">{totalPrice?.toFixed(2)} €</TableCell>
              </TableRow>
              {discount > 0 && (
                <TableRow>
                  <TableCell colSpan={3}>Rabatt ({discount}%):</TableCell>
                  <TableCell align="right">-{((totalPrice * discount) / 100).toFixed(2)} €</TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell colSpan={3} sx={{ fontWeight: 'bold', borderTop: '2px solid black' }}>
                  <strong>Gesamtbetrag:</strong>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', borderTop: '2px solid black' }}>
                  {finalPrice?.toFixed(2)} €
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={3}>davon enthaltene MwSt. (7%):</TableCell>
                <TableCell align="right">{taxAmount?.toFixed(2)} €</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
        <Box className="checkbox-container">
        <Box component="label">
          <Checkbox checked={termsAccepted} onChange={() => setTermsAccepted(!termsAccepted)} />
          <Typography fontSize="14px">
            Ich habe die <a href="/pages/agb" target="_blank">Allgemeinen Geschäftsbedingungen (AGB)</a> sowie die <a href="/pages/widerruf" target="_blank">Widerrufsbelehrung</a> gelesen und stimme diesen zu.
          </Typography>
        </Box>
        <Box component="label">
          <Checkbox checked={reviewConfirmed} onChange={() => setReviewConfirmed(!reviewConfirmed)} />
          <Typography fontSize="14px">
            Ich bestätige, das Schullogbuch vollständig überprüft zu haben und erteile hiermit einen verbindlichen und zahlungspflichtigen Druckauftrag.
          </Typography>
        </Box>
      </Box>
      </Paper>
      

      <Box sx={{ marginTop: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Button startIcon={<ArrowBackIosNewIcon />} variant="outlined" onClick={() => navigate('/pages/review', { state: { bookId } })}> Zurück </Button>
        <Button variant="contained" color="primary" onClick={handleConfirmOrder} disabled={!reviewConfirmed || !termsAccepted || numCopiesError}> Druckauftrag bestätigen </Button>
      </Box>
    </Box>
  );
};

export default Checkout;
